import * as React from "react";
import { filter } from "lodash";
import { connect } from "react-redux";
import * as moment from "moment";

import { AppShape, HurdleShape, DocTypes, LoanApplicationBorrower } from "@eave/shared/types/api";
import { Table } from "react-bootstrap";
import { Button } from "carbon-components-react";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import APIClient from "../../APIClient";
import { borrowersAscending } from "@eave/shared/utils/borrowers";

const transcriptType1040 = "1040";
const transcriptTypeW2 = "W-2";

const transcriptTypes = [transcriptType1040, transcriptTypeW2];

const endpoints = {
  [transcriptType1040]: "4506t",
  [transcriptTypeW2]: "4506t_w2",
};

const updateEndpoints = {
  [transcriptType1040]: "4506t_trigger_update",
  [transcriptTypeW2]: "4506t_w2_trigger_update",
};

const transcriptDocTypes = {
  [transcriptType1040]: DocTypes.TaxTranscript,
  [transcriptTypeW2]: DocTypes.W2Transcript,
};

function dateTimeOutput(dateTimeValue): string {
  return dateTimeValue && moment(dateTimeValue).format("M/D/YY h:mm a");
}

interface HurdleEditorProps {
  dispatch?: Function;
  app: AppShape;
  hurdle: HurdleShape;
  onHide: Function;
}

class TaxTranscriptsHurdleEditor extends React.Component<HurdleEditorProps, any> {
  constructor(props) {
    super(props);
    this.state = {
      isNew4506TButtonDisabledForBorrower: Object.keys(this.props.app.borrowers).reduce(
        (acc, be) => ({
          ...acc,
          [be]: false,
        }),
        {},
      ),
    };
  }

  componentDidMount() {
    const { borrowers } = this.props.app;
    // we check the status for all requests on load
    for (let i = 0; i < transcriptTypes.length; i += 1) {
      const t = transcriptTypes[i];
      Object.keys(borrowers).forEach(borrowerEntity => {
        this.checkTranscripts(borrowerEntity, t);
      });
    }
  }

  hasTranscript(borrowerEntity: number, type: string): any {
    const { app } = this.props;
    const docType = transcriptDocTypes[type];
    const docsOfTypeForBorrower = filter(
      app.documents,
      (d: any) => d.type === docType && d.borrower_entity === borrowerEntity,
    );
    return docsOfTypeForBorrower.length > 0;
  }

  requestTranscripts(borrowerEntity, type) {
    const endpoint = endpoints[type];
    APIClient.post(`/uw/${this.props.app.id}/${endpoint}`, { borrower: borrowerEntity }).then(
      () => {
        this.setState({
          [`borrower${borrowerEntity}${type}OrderStatus`]: "in_progress",
        });
      },
    );
  }

  checkTranscripts(borrowerEntity, type) {
    const endpoint = endpoints[type];
    APIClient.get(`/uw/${this.props.app.id}/${endpoint}?borrower=${borrowerEntity}`)
      .then((resp: any) => {
        const orderDatetime = dateTimeOutput(resp.order_datetime);
        const statusDatetime = dateTimeOutput(resp.status_datetime);

        this.setState({
          [`borrower${borrowerEntity}${type}CheckedAt`]: statusDatetime,
          [`borrower${borrowerEntity}${type}OrderStatus`]: resp.status,
          [`borrower${borrowerEntity}${type}OrderedAt`]: orderDatetime,
          [`borrower${borrowerEntity}${type}Message`]: resp.message,
        });
      })
      .catch(() => {
        this.setState({
          [`borrower${borrowerEntity}${type}CheckedAt`]: null,
          [`borrower${borrowerEntity}${type}OrderStatus`]: null,
          [`borrower${borrowerEntity}${type}OrderedAt`]: null,
          [`borrower${borrowerEntity}${type}Message`]: null,
        });
      });
  }

  updateTranscriptRequest(borrowerEntity, type) {
    const endpoint = updateEndpoints[type];
    APIClient.post(`/uw/${this.props.app.id}/${endpoint}`, { borrower: borrowerEntity }).then(
      () => {
        this.setState({
          [`borrower${borrowerEntity}${type}OrderStatus`]: "in_progress",
        });
      },
    );
  }

  requestNew4506T(borrowerEntity) {
    this.disable4506TButtonForBorrower(borrowerEntity);
    APIClient.post(`/uw/${this.props.app.id}/4506t_docusign_resend`, {
      borrower: borrowerEntity,
    }).then(() => {
      // do nothing, it's ok to reload and do this as many times as needed
      // TODO perhaps be a little smarter based on when we last triggered
    });
  }

  disable4506TButtonForBorrower(borrowerEntity) {
    this.setState(prevState => {
      const updatedNew4506TButtonForBorrower = {
        ...prevState.isNew4506TButtonDisabledForBorrower,
        [borrowerEntity]: true,
      };
      return {
        isNew4506TButtonDisabledForBorrower: updatedNew4506TButtonForBorrower,
      };
    });
  }

  signed4506T(borrowerEntity): boolean {
    const { app } = this.props;

    // we look for the certificate file in the borrower's docs.
    // there are two ways at the moment to check if borrower has signed:
    // - the legacy hellosign way, which uses prequal_package_audit_trail
    // - the docusign way, which uses prequal_package_certificate
    const prequalPackageCertificatesForBorrower = filter(
      app.documents,
      (d: any) =>
        (d.type === DocTypes.PrequalPackageCertificate ||
          d.type === DocTypes.PrequalPackageAuditTrail) &&
        d.borrower_entity === borrowerEntity,
    );

    return prequalPackageCertificatesForBorrower.length > 0;
  }

  borrowerTypeField(borrowerEntity, type, fieldName): string {
    return this.state[`borrower${borrowerEntity}${type}${fieldName}`] || "";
  }

  transcriptRequestTable(borrowerEntity) {
    const rowArray = [];

    for (let i = 0; i < transcriptTypes.length; i += 1) {
      const t = transcriptTypes[i];
      rowArray.push(
        <tr key={`transcript_${t}${borrowerEntity}`}>
          <td>{t}</td>
          <td>{this.statusAction(borrowerEntity, t)}</td>
          <td>{this.statusText(borrowerEntity, t)}</td>
          <td>{this.borrowerTypeField(borrowerEntity, t, "OrderedAt")}</td>
          <td>{this.borrowerTypeField(borrowerEntity, t, "CheckedAt")}</td>
        </tr>,
      );
    }

    // TODO we know for each borrower what years are required in the transcript in the backend
    // and use that in the actual transcript request. we could surface here
    return (
      <Table key={`transcripts_${borrowerEntity}`}>
        <thead>
          <tr>
            <th>Transcript type</th>
            <th>Action</th>
            <th>Status</th>
            <th>Order Date/Time</th>
            <th>Status Date/Time</th>
          </tr>
        </thead>
        <tbody>{rowArray}</tbody>
      </Table>
    );
  }

  statusAction(borrowerEntity, type) {
    const status = this.borrowerTypeField(borrowerEntity, type, "OrderStatus");
    let action = <div>-</div>; // default is no action

    if (status === "needs_update") {
      action = (
        <Button size="sm" onClick={this.updateTranscriptRequest.bind(this, borrowerEntity, type)}>
          Send an updated 4506-T
        </Button>
      );
    } else if (this.signed4506T(borrowerEntity) && status === "order_not_placed_yet") {
      action = (
        <Button size="sm" onClick={this.requestTranscripts.bind(this, borrowerEntity, type)}>
          Request transcript
        </Button>
      );
    }
    // TODO adhoc status check when we have a scheduled job checking

    return action;
  }

  statusText(borrowerEntity, type) {
    const status = this.borrowerTypeField(borrowerEntity, type, "OrderStatus");
    let message = <div>{status}</div>; // default is the plain status string

    if (!this.signed4506T(borrowerEntity)) {
      // it's possible the borrower signed and the docs were not uploaded,
      // but that hopefully is caught elsewhere (docusign callback fallback job)
      message = <p>Borrower has yet to sign 4506-T</p>;
    } else if (status === "needs_update") {
      const orderMessage =
        this.borrowerTypeField(borrowerEntity, type, "Message") || "< no message from Corelogic >";

      message = (
        <div>
          <p style={{ fontWeight: "bold" }}>{orderMessage}</p>
          <p>
            Please make sure an updated 4506-T has been uploaded to TRUS before attaching it to the
            existing order.
          </p>
        </div>
      );
    } else if (status === "already_processed") {
      if (this.hasTranscript(borrowerEntity, type)) {
        message = <p>Order completed, transcript available</p>;
      } else {
        // this means the process of parsing the response from corelogic
        // or uploading the doc failed. need to inspect the response and see if
        // the transcript can be extracted
        message = <p>Order completed, but transcript not available in TRUS, contact engineering</p>;
      }
    }

    return message;
  }

  renderNew4506TButton(borrowerEntity) {
    let shouldRender = false;
    for (let i = 0; i < transcriptTypes.length; i += 1) {
      const t = transcriptTypes[i];
      const status = this.borrowerTypeField(borrowerEntity, t, "OrderStatus");
      if (status === "order_not_placed_yet" || status === "needs_update") {
        shouldRender = true;
      }
    }

    if (shouldRender) {
      const disable = this.state.isNew4506TButtonDisabledForBorrower[borrowerEntity];

      return (
        <div key={`new_4506t_${borrowerEntity}`}>
          <p>
            If a new signed 4506-T is needed, make sure TRUS has up-to-date info, and click here.
            The borrower will receive an email with a docusign link. They will need to enter their
            year of birth as the access code to sign.
          </p>
          <Button disabled={disable} onClick={this.requestNew4506T.bind(this, borrowerEntity)}>
            Request a new 4506-T
          </Button>
        </div>
      );
    }

    return null;
  }

  render() {
    const { app, hurdle, onHide } = this.props;
    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        {borrowersAscending(app.borrowers).map((b: LoanApplicationBorrower) => {
          return (
            <div key={b.borrower_entity}>
              <h3 key={`title_${b.borrower_entity}`}>
                {b.first_name} {b.last_name}
              </h3>
              <div>
                {this.transcriptRequestTable(b.borrower_entity)}
                {this.renderNew4506TButton(b.borrower_entity)}
              </div>
            </div>
          );
        })}
      </HurdleView>
    );
  }
}

export default connect()(TaxTranscriptsHurdleEditor);
